export type Point = { x: number; y: number }
export type Size = { width: number; height: number }
export type Rect = Point & Size

export const clampZoom = (zoom: number, minZoom: number, maxZoom: number) => (
  Math.max(minZoom, Math.min(maxZoom, zoom))
)

/** The pan that keeps the point under `focal` (container coordinates) in place while zooming. */
export const zoomAt = (pan: Point, zoom: number, nextZoom: number, focal: Point): Point => {
  const ratio = nextZoom / zoom

  return {
    x: focal.x - (focal.x - pan.x) * ratio,
    y: focal.y - (focal.y - pan.y) * ratio,
  }
}

/** Keeps the content, plus `padding` around it, covering the container's edges. */
export const clampPan = (
  pan: Point,
  zoom: number,
  container: Size,
  content: Size,
  padding: number,
): Point => {
  const minX = container.width - (content.width + padding) * zoom
  const maxX = padding * zoom
  const minY = container.height - (content.height + padding) * zoom
  const maxY = padding * zoom

  return {
    x: Math.max(minX, Math.min(maxX, pan.x)),
    y: Math.max(minY, Math.min(maxY, pan.y)),
  }
}

/** The pan that centres the content in the container at `zoom`. */
export const centerPan = (container: Size, content: Size, zoom = 1): Point => ({
  x: (container.width - content.width * zoom) / 2,
  y: (container.height - content.height * zoom) / 2,
})

/** A point on screen, relative to the container's `origin`, in content coordinates. */
export const screenToWorld = (point: Point, origin: Point, pan: Point, zoom: number): Point => ({
  x: (point.x - origin.x - pan.x) / zoom,
  y: (point.y - origin.y - pan.y) / zoom,
})
